import React, { useRef } from 'react';
import { LogOutIcon, MaximizeIcon, MinimizeIcon } from 'lucide-react';
import { EventSetup } from './components/EventSetup';
import { HostView } from './components/HostView';
import { JoinScreen } from './components/JoinScreen';
import { ParticipantView } from './components/ParticipantView';
import { SiteFooter } from './components/SiteFooter';
import { useFullscreen } from './hooks/useFullscreen';
import { useRoom } from './hooks/useRoom';
import { useSession } from './hooks/useSession';
type BoardMode = 'auto' | 'wheel' | 'tiles';
interface AppProps {
  /** Winners leave the pool after they are drawn. */
  removeWinners?: boolean;
  /** How long the draw runs before revealing the winner. */
  spinSeconds?: number;
  /** 'auto' shows the wheel up to 20 names and the tile board above that. */
  boardMode?: BoardMode;
}
export function App({
  removeWinners = true,
  spinSeconds = 5,
  boardMode = 'auto'
}: AppProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const {
    isFullscreen,
    isFallback,
    toggle
  } = useFullscreen(shellRef);
  const {
    session,
    ready,
    busy,
    error,
    setError,
    joinAsParticipant,
    loginAsHost,
    listEvents,
    openEvent,
    closeEvent,
    renameEvent,
    leave
  } = useSession();
  const roomApi = useRoom(session);
  const shellClass = `relative flex w-full flex-col bg-ink font-sans text-body ${isFallback ? 'fixed inset-0 z-50 h-screen overflow-auto' : 'min-h-full'}`;
  if (!ready) {
    return <div className={shellClass} data-id="element-0" data-mp="33mf623uvifju">
        <div className="flex flex-1 items-center justify-center py-24 text-sm text-muted" data-id="element-1" data-mp="33nggq77z9011">
          Connecting…
        </div>
        <SiteFooter data-id="element-2" data-mp="38nuodo5o27yj" />
      </div>;
  }
  if (!session) {
    return <div ref={shellRef} className={shellClass} data-id="element-3" data-mp="3dlnwwnufze1z">
        <JoinScreen busy={busy} error={error} onClearError={() => setError(null)} onJoin={joinAsParticipant} onHostLogin={loginAsHost} data-id="element-4" data-mp="nvsvh92bsvy2" />
        <SiteFooter data-id="element-5" data-mp="36hvn4muyo06" />
      </div>;
  }
  const isHost = session.role === 'admin';
  if (isHost && !session.roomCode) {
    return <div ref={shellRef} className={shellClass} data-id="element-6" data-mp="3ubrhck3tkg3z">
        <EventSetup hostEmail={session.name} busy={busy} error={error} listEvents={listEvents} onOpen={openEvent} onSignOut={leave} data-id="element-7" data-mp="3uf5g7id9y76o" />
        <SiteFooter data-id="element-8" data-mp="3gle7udba4tcj" />
      </div>;
  }
  return <div ref={shellRef} className={shellClass} data-id="element-9" data-mp="3gm665vw9h56i">
      <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-4 sm:px-8" data-id="element-10" data-mp="3bmbf9schkflg">
        <div className="min-w-0" data-id="element-11" data-mp="3qaxs9nen4v9i">
          <h1 className="truncate text-base font-semibold tracking-tight text-white sm:text-lg" data-id="element-12" data-mp="3qc948ai8p9bp">
            {roomApi.room.name}
          </h1>
          <p className="mt-0.5 truncate text-xs text-muted" data-id="element-13" data-mp="3limsckjwh9gy">
            Code <span className="tracking-[0.14em] text-gold" data-id="element-14" data-mp="2xtbyzo4vqhcu">{session.roomCode}</span> ·{' '}
            {isHost ? 'Administrator' : `Watching as ${session.name}`}
            {roomApi.room.closed ? ' · event closed' : !roomApi.room.chatEnabled ? ' · chat paused' : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2" data-id="element-15" data-mp="yfvazbw0yyje">
          <button type="button" onClick={toggle} aria-pressed={isFullscreen} className="inline-flex items-center gap-2 rounded-full border border-line px-3.5 py-2 text-xs font-semibold text-body transition-colors duration-150 ease-emphasis hover:border-gold hover:text-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-16" data-mp="yheq2z5y33jl">
            {isFullscreen ? <MinimizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-17" data-mp="1m44n1m3fs7xc" /> : <MaximizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-18" data-mp="6o5vnmi1gqjs" />}
            <span className="hidden sm:inline" data-id="element-19" data-mp="aqiqvgz5t62b">
              {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            </span>
          </button>
          <button type="button" onClick={leave} className="inline-flex items-center gap-2 rounded-full border border-line px-3.5 py-2 text-xs font-semibold text-body transition-colors duration-150 ease-emphasis hover:border-crimson hover:text-crimson focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-20" data-mp="qygjpx44du12">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-21" data-mp="19v898oa995dk" />
            <span className="hidden sm:inline" data-id="element-22" data-mp="14xycsoog7tg8">{isHost ? 'Sign out' : 'Leave'}</span>
          </button>
        </div>
      </header>

      {roomApi.error && <p role="alert" className="border-b border-line bg-raised px-5 py-2 text-xs text-crimson sm:px-8" data-id="element-23" data-mp="vw8nppi1h6lm">
          {roomApi.error}
        </p>}

      {isHost ? <HostView session={session} roomApi={roomApi} removeWinners={removeWinners} spinSeconds={spinSeconds} boardMode={boardMode} onCloseEvent={closeEvent} onRenameSession={renameEvent} data-id="element-24" data-mp="3gmh96n2xzud1" /> : <ParticipantView session={session} roomApi={roomApi} data-id="element-25" data-mp="203xpdml1hsrh" />}

      <SiteFooter data-id="element-26" data-mp="254bnocwu9ocb" />
    </div>;
}